import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Shield, CheckCircle, Clock, XCircle, Upload, Star } from 'lucide-react';
import Navbar from '../components/Navbar';
import API from '../services/api';
import { useAuth } from '../context/AuthContext';

const BadgeInfo = ({ niveau, label, desc, couleur, icon }) => (
  <div style={{ border: `2px solid ${couleur}20`, background: `${couleur}08` }}
    className="rounded-2xl p-5">
    <div className="flex items-center gap-3 mb-3">
      <div style={{ background: couleur }} className="w-10 h-10 rounded-full flex items-center justify-center">
        <span className="text-white font-black text-sm">{icon}</span>
      </div>
      <div>
        <p style={{ color: couleur }} className="font-black text-sm">Niveau {niveau}</p>
        <p className="font-bold text-gray-900 text-sm">{label}</p>
      </div>
    </div>
    <p className="text-gray-500 text-xs">{desc}</p>
  </div>
);

const Verification = () => {
  const { user } = useAuth();
  const [statut, setStatut] = useState(null);
  const [loading, setLoading] = useState(true);
  const [niveau, setNiveau] = useState(1);
  const [document, setDocument] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const [succes, setSucces] = useState('');
  const [erreur, setErreur] = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    API.get('/verifications/mon-statut')
      .then(res => setStatut(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSoumettre = async (e) => {
    e.preventDefault();
    setEnvoi(true);
    setErreur('');
    try {
      const formData = new FormData();
      formData.append('niveau', niveau);
      if (document) formData.append('document', document);

      await API.post('/verifications/soumettre', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSucces('Demande envoyée avec succès ! L\'admin la traitera sous 24-48h.');
      // Recharger le statut
      const res = await API.get('/verifications/mon-statut');
      setStatut(res.data);
    } catch (err) {
      setErreur(err.response?.data?.message || 'Erreur lors de l\'envoi');
    } finally {
      setEnvoi(false);
    }
  };

  const getBadgeLabel = (niveau) => {
    const labels = { 1: 'Profil Vérifié', 2: 'Identité Vérifiée', 3: 'Pro Certifié' };
    return labels[niveau] || 'Non vérifié';
  };

  const getBadgeCouleur = (niveau) => {
    const couleurs = { 1: '#3b82f6', 2: '#8b5cf6', 3: '#F5A623' };
    return couleurs[niveau] || '#9ca3af';
  };

  const getBadgeIcon = (niveau) => {
    const icons = { 1: '✓', 2: '✓✓', 3: '★' };
    return icons[niveau] || '?';
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50"><Navbar />
      <div className="text-center py-20 text-gray-400">Chargement...</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div style={{ background: '#0A0A0A' }} className="py-3 px-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-white text-xl font-black flex items-center gap-2">
            <Shield size={20} style={{ color: '#F5A623' }} />
            Vérification du compte
          </h1>
          <p style={{ color: '#F5A623' }} className="text-xs mt-1">
            Obtenez un badge de confiance pour vos annonces
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">

        {/* Badge actuel */}
        {statut?.niveau_verification > 0 && (
          <div style={{ background: getBadgeCouleur(statut.niveau_verification) + '15',
            border: `2px solid ${getBadgeCouleur(statut.niveau_verification)}30` }}
            className="rounded-2xl p-6 mb-6 flex items-center gap-4">
            <div style={{ background: getBadgeCouleur(statut.niveau_verification) }}
              className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white font-black text-2xl">
                {getBadgeIcon(statut.niveau_verification)}
              </span>
            </div>
            <div>
              <p className="text-gray-500 text-xs mb-1">Votre badge actuel</p>
              <p style={{ color: getBadgeCouleur(statut.niveau_verification) }}
                className="font-black text-xl">
                {getBadgeLabel(statut.niveau_verification)}
              </p>
              <p className="text-gray-500 text-sm">Niveau {statut.niveau_verification} — Badge actif ✅</p>
            </div>
          </div>
        )}

        {/* Statut dernière demande */}
        {statut?.derniere_demande && (
          <div className={`rounded-2xl p-4 mb-6 flex items-center gap-3 ${
            statut.derniere_demande.statut === 'en_attente' ? 'bg-yellow-50 border border-yellow-200' :
            statut.derniere_demande.statut === 'valide' ? 'bg-green-50 border border-green-200' :
            'bg-red-50 border border-red-200'
          }`}>
            {statut.derniere_demande.statut === 'en_attente' && <Clock size={20} className="text-yellow-500" />}
            {statut.derniere_demande.statut === 'valide' && <CheckCircle size={20} className="text-green-500" />}
            {statut.derniere_demande.statut === 'refuse' && <XCircle size={20} className="text-red-500" />}
            <div>
              <p className="font-bold text-sm text-gray-900">
                {statut.derniere_demande.statut === 'en_attente' && '⏳ Demande en cours de traitement'}
                {statut.derniere_demande.statut === 'valide' && '✅ Demande validée'}
                {statut.derniere_demande.statut === 'refuse' && '❌ Demande refusée'}
              </p>
              {statut.derniere_demande.commentaire && (
                <p className="text-xs text-gray-500 mt-1">{statut.derniere_demande.commentaire}</p>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

          {/* Les 3 niveaux */}
          <div className="space-y-4">
            <h2 className="font-black text-gray-900 text-lg">Les 3 niveaux de badge</h2>
            <BadgeInfo niveau={1} label="Profil Vérifié" couleur="#3b82f6" icon="✓"
              desc="Votre profil est vérifié par l'équipe JJ Cars. Idéal pour les particuliers." />
            <BadgeInfo niveau={2} label="Identité Vérifiée" couleur="#8b5cf6" icon="✓✓"
              desc="Votre identité a été vérifiée avec une pièce d'identité. Plus de crédibilité." />
            <BadgeInfo niveau={3} label="Pro Certifié" couleur="#F5A623" icon="★"
              desc="Réservé aux professionnels avec documents d'entreprise (RCCM). Maximum de confiance." />
          </div>

          {/* Formulaire de demande */}
          <div>
            <h2 className="font-black text-gray-900 text-lg mb-4">Faire une demande</h2>

            {statut?.derniere_demande?.statut === 'en_attente' ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-6 text-center">
                <Clock size={40} className="mx-auto mb-3 text-yellow-500" />
                <p className="font-bold text-gray-900">Demande en cours</p>
                <p className="text-gray-500 text-sm mt-2">
                  Votre demande est en cours de traitement par l'administrateur. Délai : 24-48h.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSoumettre}
                className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100 space-y-4">
                {succes && (
                  <div className="bg-green-50 border border-green-200 text-green-600 p-3 rounded-xl text-sm">
                    {succes}
                  </div>
                )}
                {erreur && (
                  <div className="bg-red-50 border border-red-200 text-red-600 p-3 rounded-xl text-sm">
                    {erreur}
                  </div>
                )}

                {/* Choix du niveau */}
                <div>
                  <label className="block text-xs font-bold text-gray-500 mb-2">
                    Niveau de vérification souhaité
                  </label>
                  <div className="space-y-2">
                    {[
                      { val: 1, label: 'Profil Vérifié', couleur: '#3b82f6', icon: '✓' },
                      { val: 2, label: 'Identité Vérifiée', couleur: '#8b5cf6', icon: '✓✓' },
                      { val: 3, label: 'Pro Certifié', couleur: '#F5A623', icon: '★' }
                    ].map(n => (
                      <label key={n.val}
                        style={{ borderColor: niveau === n.val ? n.couleur : '#e5e7eb',
                          background: niveau === n.val ? n.couleur + '10' : 'white' }}
                        className="flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition">
                        <input type="radio" name="niveau" value={n.val}
                          checked={niveau === n.val}
                          onChange={() => setNiveau(n.val)}
                          className="hidden" />
                        <div style={{ background: n.couleur }}
                          className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-xs font-black">{n.icon}</span>
                        </div>
                        <span className="font-semibold text-sm text-gray-900">{n.label}</span>
                        {niveau === n.val && (
                          <CheckCircle size={16} style={{ color: n.couleur }} className="ml-auto" />
                        )}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Upload document */}
                <div>
                  <label className="block text-xs font-bold text-gray-500 mb-2">
                    Document justificatif (optionnel)
                    <span className="font-normal ml-1">— CNI, passeport ou RCCM</span>
                  </label>
                  <div
                    onClick={() => fileRef.current.click()}
                    style={{ border: '2px dashed #e5e7eb' }}
                    className="rounded-xl p-4 text-center cursor-pointer hover:border-yellow-400 transition"
                  >
                    {document ? (
                      <div className="flex items-center justify-center gap-2">
                        <Upload size={16} className="text-green-500" />
                        <p className="text-sm text-green-600 font-semibold">{document.name}</p>
                      </div>
                    ) : (
                      <div>
                        <Upload size={20} className="mx-auto mb-1 text-gray-300" />
                        <p className="text-xs text-gray-400">Cliquez pour ajouter un document</p>
                        <p className="text-xs text-gray-300 mt-1">JPG, PNG, PDF — 10MB max</p>
                      </div>
                    )}
                  </div>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) => setDocument(e.target.files[0])}
                    className="hidden"
                  />
                </div>

                <button
                  type="submit"
                  disabled={envoi}
                  style={{ background: '#F5A623' }}
                  className="w-full text-black py-3 rounded-xl font-black hover:opacity-90 transition disabled:opacity-50"
                >
                  {envoi ? 'Envoi en cours...' : 'Envoyer la demande'}
                </button>

                <p className="text-xs text-gray-400 text-center">
                  L'équipe JJ Cars traitera votre demande sous 24-48h
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Verification;    